import React, { useState, useEffect } from 'react';
import { Mic, MicOff, Plus } from 'lucide-react';
import { motion } from 'framer-motion';
import { contactService, aiService } from '../services/api';

const AddContact = ({ onContactAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    linkedin_url: '',
    connection_origin: '',
    interests: '',
    conversation_notes: '',
    birthday: '',
    contact_importance: 'medium',
  });
  const [voiceMemo, setVoiceMemo] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [recognition, setRecognition] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);

  const [importantDates, setImportantDates] = useState([]);


  // Initialize speech recognition on component mount
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';

      let finalTranscriptBuffer = '';

      recognitionInstance.onresult = (event) => {
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcriptPiece = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscriptBuffer += transcriptPiece + ' ';
          } else {
            interimTranscript += transcriptPiece;
          }
        }

        setTranscript(finalTranscriptBuffer);
        setVoiceMemo(finalTranscriptBuffer + interimTranscript);
      };

      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setError(`Voice recognition error: ${event.error}`);
        setIsRecording(false);
      };

      recognitionInstance.onend = () => {
        setIsRecording(false);
      };

      setRecognition(recognitionInstance);
    } else {
      setError('Speech recognition is not supported in your browser. Please use Chrome, Edge, or Safari.');
    }

    return () => {
      if (recognition) {
        recognition.stop();
      }
    };
  }, []);

  const addImportantDate = () => {
    setImportantDates([...importantDates, {
      id: Date.now(),
      date_type: 'custom',
      date_value: '',
      label: '',
      notes: '',
      recurring: true
    }]);
  };

  const updateImportantDate = (id, field, value) => {
    setImportantDates(importantDates.map(date => 
      date.id === id ? { ...date, [field]: value } : date
    ));
  };

  const removeImportantDate = (id) => {
    setImportantDates(importantDates.filter(date => date.id !== id));
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleVoiceMemo = async () => {
    if (isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
    } else {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        setAudioChunks(chunks);
        setIsProcessing(true);
        
        try {
          const result = await aiService.transcribeAndExtract(audioBlob);
          setTranscript(result.transcript);
          setVoiceMemo(result.transcript);
          
          if (result.extracted_data) {
            const data = JSON.parse(result.extracted_data);
            setFormData({
              name: data.name || '',
              company: data.company || '',
              role: data.role || '',
              linkedin_url: formData.linkedin_url,
              connection_origin: data.connection_origin || '',
              interests: Array.isArray(data.interests) ? data.interests.join(', ') : data.interests || '',
              conversation_notes: data.conversation_notes || '',
              birthday: data.birthday || '',
              contact_importance: data.contact_importance || 'medium',
            });
            setSuccess('Voice memo processed! Review the details below.');
          }
        } catch (error) {
          console.error('Failed to process audio:', error);
          setError('Failed to process audio. Please try again.');
        } finally {
          setIsProcessing(false);
        }
      };

      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
    }
  };

  const processVoiceMemo = async () => {
    if (!transcript.trim()) {
      setError('No transcript to process. Please record something first.');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const result = await aiService.processVoiceMemo(transcript);
      const data = JSON.parse(result.extracted_data);
      
      setFormData({
        name: data.name || formData.name,
        company: data.company || formData.company,
        role: data.role || formData.role,
        linkedin_url: formData.linkedin_url,
        connection_origin: data.connection_origin || formData.connection_origin,
        interests: Array.isArray(data.interests) 
          ? data.interests.join(', ') 
          : data.interests || formData.interests,
        conversation_notes: data.conversation_notes || formData.conversation_notes,
        birthday: data.birthday || formData.birthday,
        contact_importance: data.contact_importance || formData.contact_importance,
      });

      setSuccess('Voice memo processed! Review and edit the details below.');
    } catch (error) {
      console.error('Failed to process voice memo:', error);
      setError('Failed to process voice memo. Please try again or enter details manually.');
    } finally {
      setIsProcessing(false);
    }
  };

  const clearVoiceMemo = () => {
    setVoiceMemo('');
    setTranscript('');
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // First, create the contact
      const response = await contactService.createContact(formData);
      const newContactId = response.contact.id;
      
      // Then, add all important dates if any exist
      if (importantDates.length > 0) {
        for (const date of importantDates) {
          if (date.date_value) { // Only save if date is filled
            await contactService.addImportantDate(newContactId, {
              date_type: date.date_type,
              date_value: date.date_value,
              label: date.label,
              notes: date.notes || '',
              recurring: date.recurring
            });
          }
        }
      }
      
      setSuccess('Contact and important dates added successfully!');
      
      // Reset form
      setFormData({
        name: '',
        company: '',
        role: '',
        linkedin_url: '',
        connection_origin: '',
        interests: '',
        conversation_notes: '',
        birthday: '',
        contact_importance: 'medium',
      });
      setVoiceMemo('');
      setTranscript('');
      setImportantDates([]); // Clear important dates too!
      
      if (onContactAdded) {
        onContactAdded();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '900px', margin: '0 auto' }}>
      <style>
        {`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.3; }
          }
        `}
      </style>
      
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontFamily: "'Syne', sans-serif", fontWeight: 800, color: '#0f172a', marginBottom: '0.5rem' }}>
          Add New Contact
        </h1>
        <p style={{ color: '#475569', fontSize: '1rem' }}>
          Add contacts manually or use voice memo for quick entry
        </p>
      </div>

      {success && (
        <div style={{ background: '#d1fae5', color: '#065f46', padding: '0.875rem', borderRadius: '12px', marginBottom: '1rem', fontSize: '0.95rem', borderLeft: '4px solid #10b981' }}>
          {success}
        </div>
      )}
      
      {error && (
        <div style={{ background: '#fee2e2', color: '#dc2626', padding: '0.875rem', borderRadius: '12px', marginBottom: '1rem', fontSize: '0.95rem', borderLeft: '4px solid #dc2626' }}>
          {error}
        </div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        style={{ background: '#ffffff', borderRadius: '16px', padding: '2rem', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', border: '1px solid #e2e8f0', marginBottom: '1.5rem' }}
      >
        <h2 style={{ fontSize: '1.25rem', fontFamily: "'Syne', sans-serif", fontWeight: 700, color: '#0f172a', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Mic size={24} />
          Voice Memo (Quick Add)
        </h2>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: '1.5rem', background: '#f8fafc', borderRadius: '12px', border: '2px dashed #cbd5e1' }}>
          {isRecording && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.75rem', background: '#fee2e2', borderRadius: '8px', color: '#dc2626', fontSize: '0.9rem', fontWeight: 500 }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#dc2626', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
              Recording... Speak naturally about your networking conversation
            </div>
          )}

          <button
            type="button"
            onClick={handleVoiceMemo}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '1rem 1.5rem',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 200ms',
              fontFamily: "'Syne', sans-serif",
              background: isRecording ? '#ef4444' : '#10b981',
              color: 'white',
              boxShadow: isRecording ? '0 4px 12px rgba(239, 68, 68, 0.4)' : '0 4px 12px rgba(16, 185, 129, 0.4)',
            }}
            onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; }}
          >
            {isRecording ? <MicOff size={20} /> : <Mic size={20} />}
            {isRecording ? 'Stop Recording' : 'Start Voice Memo'}
          </button>
          
          {voiceMemo && (
            <>
              <div style={{ padding: '1rem', background: '#ffffff', borderRadius: '8px', fontSize: '0.9rem', color: '#475569', maxHeight: '150px', overflowY: 'auto' }}>
                <strong>Transcript:</strong> {voiceMemo}
              </div>
              
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={processVoiceMemo}
                  disabled={isProcessing}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.25rem',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                    transition: 'all 200ms',
                    fontFamily: "'Syne', sans-serif",
                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.4)',
                    opacity: isProcessing ? 0.5 : 1,
                    flex: 1,
                  }}
                  onMouseEnter={(e) => { if (!isProcessing) e.target.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { if (!isProcessing) e.target.style.transform = 'translateY(0)'; }}
                >
                  {isProcessing ? 'Processing...' : 'Extract Contact Info'}
                </button>
                
                <button
                  type="button"
                  onClick={clearVoiceMemo}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    padding: '0.75rem 1.25rem',
                    border: 'none',
                    borderRadius: '12px',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'all 200ms',
                    fontFamily: "'Syne', sans-serif",
                    background: '#64748b',
                    color: 'white',
                    boxShadow: '0 4px 12px rgba(100, 116, 139, 0.4)',
                  }}
                  onMouseEnter={(e) => { e.target.style.transform = 'translateY(-2px)'; }}
                  onMouseLeave={(e) => { e.target.style.transform = 'translateY(0)'; }}
                >
                  Clear
                </button>
              </div>
            </>
          )}
          
          <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
            💡 Try: "I just had a call with Sarah Johnson. She's a product designer at Nike. We discussed rock climbing and UI design trends. She's looking for freelance work and we met at the Bentley alumni networking event."
          </p>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        style={{ background: '#ffffff', borderRadius: '16px', padding: '2rem', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', border: '1px solid #e2e8f0', marginBottom: '1.5rem' }}
      >
        <h2 style={{ fontSize: '1.25rem', fontFamily: "'Syne', sans-serif", fontWeight: 700, color: '#0f172a', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Plus size={24} />
          Contact Details
        </h2>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: '1rem',
                transition: 'all 200ms',
                fontFamily: 'inherit',
                background: '#f8fafc',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.background = '#ffffff';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = '#f8fafc';
                e.target.style.boxShadow = 'none';
              }}
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
                Company
              </label>
              <input
                type="text"
                name="company"
                value={formData.company}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  transition: 'all 200ms',
                  fontFamily: 'inherit',
                  background: '#f8fafc',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.background = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.background = '#f8fafc';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
                Role/Title
              </label>
              <input
                type="text"
                name="role"
                value={formData.role}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  transition: 'all 200ms',
                  fontFamily: 'inherit',
                  background: '#f8fafc',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.background = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.background = '#f8fafc';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
              LinkedIn URL
            </label>
            <input
              type="url"
              name="linkedin_url"
              value={formData.linkedin_url}
              onChange={handleChange}
              placeholder="https://linkedin.com/in/..."
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: '1rem',
                transition: 'all 200ms',
                fontFamily: 'inherit',
                background: '#f8fafc',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.background = '#ffffff';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = '#f8fafc';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
              How did you connect?
            </label>
            <input
              type="text"
              name="connection_origin"
              value={formData.connection_origin}
              onChange={handleChange}
              placeholder="e.g., Bentley Alumni Event, Career Fair, LinkedIn"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: '1rem',
                transition: 'all 200ms',
                fontFamily: 'inherit',
                background: '#f8fafc',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.background = '#ffffff';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = '#f8fafc';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
              Interests & Topics
            </label>
            <input
              type="text"
              name="interests"
              value={formData.interests}
              onChange={handleChange}
              placeholder="e.g., Basketball, AI, Gaming, Hiking"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: '1rem',
                transition: 'all 200ms',
                fontFamily: 'inherit',
                background: '#f8fafc',
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.background = '#ffffff';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = '#f8fafc';
                e.target.style.boxShadow = 'none';
              }}
            />
            <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
              Comma-separated interests or topics discussed
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
                Birthday
              </label>
              <input
                type="date"
                name="birthday"
                value={formData.birthday}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  transition: 'all 200ms',
                  fontFamily: 'inherit',
                  background: '#f8fafc',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.background = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.background = '#f8fafc';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                Optional: Their birthday for follow-ups
              </p>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
                Contact Importance
              </label>
              <select
                name="contact_importance"
                value={formData.contact_importance}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.875rem 1rem',
                  border: '2px solid #e2e8f0',
                  borderRadius: '12px',
                  fontSize: '1rem',
                  transition: 'all 200ms',
                  fontFamily: 'inherit',
                  background: '#f8fafc',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#2563eb';
                  e.target.style.background = '#ffffff';
                  e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e2e8f0';
                  e.target.style.background = '#f8fafc';
                  e.target.style.boxShadow = 'none';
                }}
              >
                <option value="low">Low Priority</option>
                <option value="medium">Medium Priority</option>
                <option value="high">High Priority</option>
                <option value="critical">Critical Contact</option>
              </select>
              <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.5rem' }}>
                How important is this contact to you?
              </p>
            </div>
            <div style={{ marginBottom: '1.5rem' }}>
  <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
    Important Dates
  </label>
  
  {importantDates.map((date, index) => (
    <div key={date.id} style={{ marginBottom: '1rem', padding: '1rem', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
        <select
          value={date.date_type}
          onChange={(e) => updateImportantDate(date.id, 'date_type', e.target.value)}
          style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}
        >
          <option value="birthday">Birthday</option>
          <option value="anniversary">Anniversary</option>
          <option value="work_anniversary">Work Anniversary</option>
          <option value="custom">Custom</option>
        </select>
        
        <input
          type="date"
          value={date.date_value}
          onChange={(e) => updateImportantDate(date.id, 'date_value', e.target.value)}
          style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}
        />
      </div>
      
      <input
        type="text"
        placeholder="Label (e.g., 'Wedding Anniversary')"
        value={date.label}
        onChange={(e) => updateImportantDate(date.id, 'label', e.target.value)}
        style={{ width: '100%', padding: '0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '0.5rem' }}
      />
      
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem' }}>
          <input
            type="checkbox"
            checked={date.recurring}
            onChange={(e) => updateImportantDate(date.id, 'recurring', e.target.checked)}
          />
          Recurring annually
        </label>
        
        <button
          type="button"
          onClick={() => removeImportantDate(date.id)}
          style={{ padding: '0.25rem 0.75rem', background: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
        >
          Remove
        </button>
      </div>
    </div>
  ))}
  
  <button
    type="button"
    onClick={addImportantDate}
    style={{ padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '0.9rem' }}
  >
    + Add Important Date
  </button>
</div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', color: '#0f172a', fontWeight: 500, fontSize: '0.95rem' }}>
              Conversation Notes
            </label>
            <textarea
              name="conversation_notes"
              value={formData.conversation_notes}
              onChange={handleChange}
              placeholder="What did you talk about? Any key details or action items?"
              style={{
                width: '100%',
                padding: '0.875rem 1rem',
                border: '2px solid #e2e8f0',
                borderRadius: '12px',
                fontSize: '1rem',
                transition: 'all 200ms',
                fontFamily: 'inherit',
                background: '#f8fafc',
                outline: 'none',
                minHeight: '120px',
                resize: 'vertical',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#2563eb';
                e.target.style.background = '#ffffff';
                e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#e2e8f0';
                e.target.style.background = '#f8fafc';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
              padding: '0.875rem 1.5rem',
              border: 'none',
              borderRadius: '12px',
              fontSize: '1rem',
              fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 200ms',
              fontFamily: "'Syne', sans-serif",
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
              width: '100%',
              opacity: loading ? 0.5 : 1,
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.5)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
              }
            }}
          >
            <Plus size={20} />
            {loading ? 'Adding Contact...' : 'Add Contact'}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default AddContact;